import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import { toast } from "react-toastify";
import {
  getWhatsapp,
  createWhatsapp,
  updateWhatsapp,
  restartWhatsapp,
} from "../../services/whatsapp.api";
import toastError from "../../errors/toastError";
import FacebookOAuthConnect from "./FacebookOAuthConnect";

/**
 * Modal DEDICADO para conectar Facebook Messenger / Instagram (0373 FB1, camino A nativo).
 * Clonado de WhatsAppOficialModal (WABA): mismo tratamiento (credenciales Graph, sin QR, estado
 * derivado). Un solo modal cubre ambos canales — el prop `provider` ("facebook" | "instagram")
 * decide el título, el campo Instagram Account ID y el provider persistido.
 *
 * "Conectar y validar" persiste las credenciales y dispara initSession (GET /me con el page token)
 * en el backend. La recepción real se confirma luego con un mensaje de prueba (aviso en la tarjeta).
 */

const EMPTY = {
  name: "",
  facebookPageId: "",
  facebookPageToken: "",
  facebookUserToken: "",
  instagramAccountId: "",
  verifyToken: "",
  appSecret: "",
  isDefault: false,
};

const WhatsAppFacebookModal = ({ open, onClose, whatsAppId, onSave, provider = "facebook" }) => {
  const [values, setValues] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const isInstagram = provider === "instagram";
  const channelLabel = isInstagram ? "Instagram" : "Facebook Messenger";

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!open || !whatsAppId) {
        setValues(EMPTY);
        return;
      }
      try {
        setLoading(true);
        const { data } = await getWhatsapp(whatsAppId, { session: 0 });
        if (!mounted) return;
        setValues({
          name: data.name || "",
          facebookPageId: data.facebookPageId || "",
          facebookPageToken: data.facebookPageToken || "",
          facebookUserToken: data.facebookUserToken || "",
          instagramAccountId: data.instagramAccountId || "",
          verifyToken: data.verifyToken || "",
          appSecret: data.appSecret || "",
          isDefault: !!data.isDefault,
        });
      } catch (err) {
        if (mounted) toastError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [open, whatsAppId]);

  const setField = (key) => (e) =>
    setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const handleClose = useCallback(() => {
    setValues(EMPTY);
    setSaving(false);
    onClose();
  }, [onClose]);

  const handleConnect = async () => {
    const faltantes = [];
    if (!values.name.trim()) faltantes.push("Nombre");
    if (!values.facebookPageId.trim()) faltantes.push("Page ID");
    if (!values.facebookPageToken.trim()) faltantes.push("Page Access Token");
    if (!values.verifyToken.trim()) faltantes.push("Verify Token");
    if (isInstagram && !values.instagramAccountId.trim()) faltantes.push("Instagram Account ID");
    if (faltantes.length > 0) {
      toast.error(`❌ Faltan credenciales: ${faltantes.join(", ")}`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: values.name.trim(),
        provider,
        status: "OPENING",
        isDefault: values.isDefault,
        facebookPageId: values.facebookPageId.trim(),
        facebookPageToken: values.facebookPageToken.trim(),
        facebookUserToken: values.facebookUserToken.trim(),
        instagramAccountId: values.instagramAccountId.trim(),
        verifyToken: values.verifyToken.trim(),
        appSecret: values.appSecret.trim(),
      };

      if (whatsAppId) {
        await updateWhatsapp(whatsAppId, payload);
        await restartWhatsapp(whatsAppId);
      } else {
        await createWhatsapp(payload);
      }

      toast.success(
        "✅ Conexión guardada. Validando credenciales con Meta… Para completar, escribe un mensaje de prueba desde la página (ver aviso en la tarjeta)."
      );
      if (onSave) onSave();
      handleClose();
    } catch (err) {
      toastError(err);
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {whatsAppId ? "Editar conexión" : "Conectar"} {channelLabel} — Meta Graph API
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {!whatsAppId && (
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom style={{ color: "#1877F2", fontWeight: 600 }}>
                  🚀 Conexión con un clic (recomendado)
                </Typography>
                <FacebookOAuthConnect
                  provider={provider}
                  onConnected={() => {
                    if (onSave) onSave();
                    handleClose();
                  }}
                />
                <Typography variant="caption" color="textSecondary">
                  ¿Prefieres pegar los tokens a mano? Completa el formulario:
                </Typography>
              </Box>
            )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" style={{ color: "#666" }}>
                Ingresa las credenciales de tu página / cuenta (Meta for Developers → Messenger /
                Instagram → configuración). Al conectar, se validan con Meta; luego suscribe el
                webhook en la app de Meta apuntando a esta conexión.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Nombre de la conexión"
                variant="outlined"
                margin="dense"
                fullWidth
                required
                autoFocus
                value={values.name}
                onChange={setField("name")}
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" mt={1}>
                <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#1877F2" }}>
                  🔑 Credenciales de {channelLabel} (Meta Graph API)
                </span>
              </Box>
              <Divider style={{ marginTop: 6 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Page ID"
                helperText="ID de la página de Facebook"
                variant="outlined"
                margin="dense"
                fullWidth
                value={values.facebookPageId}
                onChange={setField("facebookPageId")}
              />
            </Grid>

            {isInstagram && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Instagram Account ID"
                  helperText="ID de la cuenta profesional de Instagram vinculada"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  value={values.instagramAccountId}
                  onChange={setField("instagramAccountId")}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                label="Verify Token (webhook)"
                helperText="El mismo que registrarás en Meta"
                variant="outlined"
                margin="dense"
                fullWidth
                value={values.verifyToken}
                onChange={setField("verifyToken")}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Page Access Token"
                helperText="Token permanente de la página (Bearer). Se guarda en el servidor."
                variant="outlined"
                margin="dense"
                fullWidth
                value={values.facebookPageToken}
                onChange={setField("facebookPageToken")}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="User Token (opcional)"
                helperText="Token de usuario del OAuth para gestión/renovación de páginas."
                variant="outlined"
                margin="dense"
                fullWidth
                value={values.facebookUserToken}
                onChange={setField("facebookUserToken")}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="App Secret (opcional)"
                helperText="Valida la firma del webhook (X-Hub-Signature-256). Recomendado en producción."
                variant="outlined"
                margin="dense"
                fullWidth
                value={values.appSecret}
                onChange={setField("appSecret")}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={values.isDefault}
                    onChange={(e) =>
                      setValues((prev) => ({ ...prev, isDefault: e.target.checked }))
                    }
                  />
                }
                label="Predeterminada"
              />
            </Grid>
          </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleConnect}
          variant="contained"
          color="primary"
          disabled={saving || loading}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : "Conectar y validar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsAppFacebookModal;
